/**
 * Compile a theme into CSS custom properties.
 *
 * The four colour variables are the ones the live sites already render, and they
 * are emitted in the same order with the same names, so an un-themed site
 * produces the same stylesheet it did before the theme engine existed.
 *
 * Header, nav, type and caption variables are read by the public header and
 * page chrome. The defaults match the old hardcoded sizes, so an unedited
 * theme still paints the same.
 */

import { cssForTheme, fontFaceCss } from '../library/resolve.js';
import { themeWithOverrides } from './document.js';
import { cssLength } from './header-modules.js';

/**
 * @param {Object} theme
 * @returns {Object} CSS variable name -> value
 */
export function themeCssVars(theme, library) {
    const colors = theme?.colors || {};
    const header = theme?.header || {};
    const type = theme?.type || {};
    const chrome = theme?.chrome || {};

    const vars = {
        // Legacy four. Order and names must not change.
        '--color-side-menu-text': colors.sideMenuText,
        '--color-side-menu-background': colors.sideMenuBackground,
        '--color-background': colors.background,
        '--color-text': colors.text,
    };

    if (colors.headerBackground) vars['--color-header-background'] = colors.headerBackground;
    if (colors.headerText) vars['--color-header-text'] = colors.headerText;
    if (colors.caption) vars['--caption-color'] = colors.caption;

    // Fonts. The defaults equal the values in styles/globals.css, so emitting
    // them changes nothing until someone picks a different font.
    Object.assign(vars, cssForTheme(theme, library).vars);

    if (type.bodySize != null) vars['--type-body-size'] = cssLength(type.bodySize, 'px');
    if (type.bodyLineHeight != null) vars['--type-body-line-height'] = String(type.bodyLineHeight);
    if (type.bodyWeight != null && type.bodyWeight !== '') vars['--type-body-weight'] = String(type.bodyWeight);
    if (type.bodyStyle === 'italic' || type.bodyStyle === 'normal') vars['--type-body-style'] = type.bodyStyle;
    if (type.headingWeight != null && type.headingWeight !== '') vars['--type-heading-weight'] = String(type.headingWeight);
    if (type.headingStyle === 'italic' || type.headingStyle === 'normal') vars['--type-heading-style'] = type.headingStyle;
    if (type.headingTracking != null && type.headingTracking !== '') vars['--type-heading-tracking'] = cssLength(type.headingTracking, 'em');
    if (type.headingCasing) vars['--type-heading-casing'] = type.headingCasing === 'none' ? 'none' : type.headingCasing;
    // Sansumi headings are drawn slightly squashed; any other heading face is not.
    vars['--heading-squash'] = theme?.fonts?.heading === 'sansumi' ? 'scaleY(0.95)' : 'none';

    if (header.padY != null) vars['--header-pad-y'] = cssLength(header.padY, 'px');
    if (header.logoWidth != null) vars['--header-logo-width'] = cssLength(header.logoWidth, 'px');
    if (header.logoHeight != null) vars['--header-logo-height'] = cssLength(header.logoHeight, 'em');
    if (header.logoWidthMobile != null) vars['--header-logo-width-mobile'] = cssLength(header.logoWidthMobile, 'px');
    if (header.logoHeightMobile != null) vars['--header-logo-height-mobile'] = cssLength(header.logoHeightMobile, 'px');
    if (header.navSize != null) vars['--header-nav-size'] = cssLength(header.navSize, 'em');
    if (header.navTracking != null) vars['--header-nav-tracking'] = cssLength(header.navTracking, 'em');
    if (header.navGap != null) vars['--header-nav-gap'] = cssLength(header.navGap, 'em');
    if (header.navPadX != null) vars['--header-nav-pad-x'] = cssLength(header.navPadX, 'em');
    if (header.navCasing) vars['--header-nav-casing'] = header.navCasing === 'none' ? 'none' : header.navCasing;

    if (chrome.captionTracking != null) vars['--caption-tracking'] = cssLength(chrome.captionTracking, 'em');
    if (chrome.captionCasing) vars['--caption-casing'] = chrome.captionCasing === 'none' ? 'none' : chrome.captionCasing;
    if (chrome.captionSize != null && chrome.captionSize !== '') vars['--caption-size'] = cssLength(chrome.captionSize, 'rem');
    if (chrome.captionLineHeight != null && chrome.captionLineHeight !== '') vars['--caption-leading'] = String(chrome.captionLineHeight);
    if (chrome.captionWeight != null && chrome.captionWeight !== '') vars['--caption-weight'] = String(chrome.captionWeight);
    if (chrome.captionStyle === 'italic' || chrome.captionStyle === 'normal') vars['--caption-style'] = chrome.captionStyle;
    // Sansumi is drawn slightly squashed; a picked caption face is not.
    if (vars['--font-caption']) vars['--caption-squash'] = 'none';

    const links = theme?.links || {};
    if (links.color) vars['--link-color'] = links.color;
    if (links.hoverColor) vars['--link-hover-color'] = links.hoverColor;
    if (links.underline === 'always' || links.underline === 'hover' || links.underline === 'never') {
        vars['--link-decoration'] = links.underline === 'always' ? 'underline' : 'none';
        vars['--link-hover-decoration'] = links.underline === 'never' ? 'none' : 'underline';
    }
    if (links.thickness != null && links.thickness !== '') vars['--link-thickness'] = cssLength(links.thickness, 'px');
    if (links.offset != null && links.offset !== '') vars['--link-offset'] = cssLength(links.offset, 'em');
    if (links.hoverOpacity != null && links.hoverOpacity !== '') vars['--link-hover-opacity'] = String(links.hoverOpacity);

    const pageWidth = theme?.layout?.pageWidth;
    // body turns this into --page-max-width, one px value for every element (styles/globals.css).
    vars['--page-width'] = cssLength(pageWidth == null ? 1600 : pageWidth, 'px');

    const menuCasing = theme?.menu?.navCasing;
    if (menuCasing) vars['--menu-nav-casing'] = menuCasing === 'none' ? 'none' : menuCasing;

    // Logo mode drives which of the two logo renderings the header shows.
    const logoMode = theme?.brand?.logo?.mode;
    if (logoMode) {
        vars['--logo-image-display'] = logoMode === 'image' ? 'block' : 'none';
        vars['--logo-mask-display'] = logoMode === 'mask' ? 'block' : 'none';
        vars['--logo-text-display'] = logoMode === 'text' ? 'block' : 'none';
    }

    return Object.fromEntries(Object.entries(vars).filter(([, value]) => value != null && value !== ''));
}

/**
 * Variables themeCssVars leaves out when unset. A live preview layered over the
 * served :root has to reset these to `initial` so clearing one takes effect.
 */
export const OPTIONAL_THEME_VARS = [
    '--color-header-background',
    '--color-header-text',
    '--caption-color',
    '--font-caption',
    '--caption-size',
    '--caption-leading',
    '--caption-weight',
    '--caption-style',
    '--type-body-weight',
    '--type-body-style',
    '--type-heading-weight',
    '--type-heading-style',
    '--type-heading-tracking',
    '--type-heading-casing',
    '--caption-squash',
    '--caption-tracking',
    '--caption-casing',
    '--link-color',
    '--link-hover-color',
    '--link-decoration',
    '--link-hover-decoration',
    '--link-thickness',
    '--link-offset',
    '--link-hover-opacity',
];

function declarations(vars, indent = '                    ') {
    return Object.entries(vars)
        .map(([name, value]) => `${indent}${name}: ${value};`)
        .join('\n');
}

/**
 * Full stylesheet for a theme: the base :root block plus the phone and tablet
 * override layers.
 *
 * The base block is formatted the way lib/server.js already formatted it, so
 * the emitted HTML for an un-themed site is unchanged.
 *
 * @param {Object} theme
 * @returns {string} CSS text
 */
export function themeStyleTag(theme, library) {
    const base = themeCssVars(theme, library);

    // @font-face for uploaded fonts, only the ones this theme references.
    const fontFaces = cssForTheme(theme, library).fontFaceCss;

    let css = `${fontFaces}
                :root {
${declarations(base)}
                }
            `;

    const layers = [
        { key: 'phone', media: '(max-width: 600px)' },
        { key: 'tablet', media: '(min-width: 601px) and (max-width: 1024px)' },
    ];

    layers.forEach(({ key, media }) => {
        const overrides = theme?.overrides?.[key];
        if (!overrides || Object.keys(overrides).length === 0) return;

        // Re-compile the whole theme with this layer applied, then keep only the
        // variables that actually differ from the base.
        const layered = themeCssVars(themeWithOverrides(theme, key), library);
        const changed = Object.fromEntries(
            Object.entries(layered).filter(([name, value]) => base[name] !== value)
        );
        if (Object.keys(changed).length === 0) return;

        css += `
                @media ${media} {
                    :root {
${declarations(changed, '                        ')}
                    }
                }
            `;
    });

    return css;
}

/** Attribute the boot script sets on <html> to pick an A/B theme. */
export const THEME_ATTRIBUTE = 'data-site-theme';

/**
 * Stylesheet for an A/B pool.
 *
 * `:root` stays the live theme, so server-rendered HTML is identical for every
 * visitor and stays cacheable. Each additional pooled theme is emitted behind an
 * attribute selector that the boot script flips before first paint.
 *
 * @param {Array} entries - [{ id, theme }], the first being live
 * @returns {string} CSS text
 */
export function themeStyleSheet(entries, library) {
    if (!entries || entries.length === 0) return '';

    let css = themeStyleTag(entries[0].theme, library);
    const liveFaces = new Set(cssForTheme(entries[0].theme, library).faces.map((face) => face.id));
    entries.slice(1).forEach(({ theme }) => {
        cssForTheme(theme, library).faces.forEach((face) => {
            if (liveFaces.has(face.id)) return;
            liveFaces.add(face.id);
            css = fontFaceCss([face]) + css;
        });
    });

    entries.slice(1).forEach(({ id, theme }) => {
        const base = themeCssVars(entries[0].theme, library);
        const variant = themeCssVars(theme, library);
        const changed = Object.fromEntries(
            Object.entries(variant).filter(([name, value]) => base[name] !== value)
        );
        if (Object.keys(changed).length === 0) return;

        css += `
                [${THEME_ATTRIBUTE}="${id}"] {
${declarations(changed, '                    ')}
                }
            `;
    });

    return css;
}

/**
 * Blocking script that assigns a visitor to one pooled theme and remembers it.
 *
 * Runs before paint so there is no flash of the live theme, and writes a
 * long-lived cookie so the same visitor keeps the same variant. Returns an empty
 * string when there is no pool, so a single-theme site ships no script at all.
 *
 * @param {Array<string>} ids - Pooled theme ids, the first being live
 * @param {string} [cookieName]
 * @returns {string} JavaScript source
 */
export function themeBootScript(ids, cookieName = 'site_theme') {
    if (!ids || ids.length < 2) return '';

    // Kept terse deliberately: this is inline, blocking, pre-paint code.
    return `(function(){try{var i=${JSON.stringify(ids)};` +
        `var m=document.cookie.match(/(?:^|; )${cookieName}=([^;]*)/);` +
        `var p=m?decodeURIComponent(m[1]):null;` +
        `if(i.indexOf(p)===-1){p=i[Math.floor(Math.random()*i.length)];` +
        `document.cookie='${cookieName}='+encodeURIComponent(p)+';path=/;max-age=31536000;samesite=lax';}` +
        `if(p!==i[0]){document.documentElement.setAttribute('${THEME_ATTRIBUTE}',p);}` +
        `}catch(e){}})();`;
}

export default { themeCssVars, themeStyleTag, themeStyleSheet, themeBootScript, THEME_ATTRIBUTE };
